import { useEffect, useState } from "react";
import { eq } from "drizzle-orm";
import { db } from "./db";
import * as s from "../../shared/src/schema";
import { useAuth } from "./auth";
import { useCommand, useListVersion, useStore } from "./store";
import type { ChartSpec } from "../../shared/src/protocol";
import { encodeSpec, decodeSpec } from "./chartSpec";

const TOKEN_VERSION = "dlv1";

/** Encode an array of chart specs as a copy-pasteable token: `dlv1:spec1|spec2|...` */
export function encodeDashboard(specs: ChartSpec[]): string {
  return `${TOKEN_VERSION}:${specs.map(encodeSpec).join("|")}`;
}

/** Decode a token back to specs. Returns null on malformed input.
 *  Also accepts the bare URL form (no version prefix) for backwards compatibility. */
export function decodeDashboard(input: string): ChartSpec[] | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const stripped = trimmed.startsWith(`${TOKEN_VERSION}:`)
    ? trimmed.slice(TOKEN_VERSION.length + 1)
    : trimmed;
  const parts = stripped.split("|").filter(Boolean);
  if (parts.length === 0) return null;
  const specs: ChartSpec[] = [];
  for (const p of parts) {
    const s = decodeSpec(p);
    if (!s) return null;
    specs.push(s);
  }
  return specs;
}

/** URL param form — same as token body, no version prefix (URLs are local to this app). */
export function encodeDashboardForUrl(specs: ChartSpec[]): string {
  return specs.map(encodeSpec).join("|");
}

export function decodeDashboardFromUrl(input: string | null): ChartSpec[] | null {
  if (!input) return null;
  return decodeDashboard(input);
}

// ─── Saved views (synced via users.dashboards event-sourced column) ──

export interface SavedView {
  name: string;
  token: string;
  savedAt: number;
}

function parseSavedViews(json: string | null): SavedView[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is SavedView =>
        v &&
        typeof v.name === "string" &&
        typeof v.token === "string" &&
        typeof v.savedAt === "number",
    );
  } catch {
    return [];
  }
}

/** Reactive, DB-backed saved views for the current user.
 *  Reads from the local `users.dashboards` JSON; writes go through a synced event. */
export function useSavedViews(): {
  views: SavedView[];
  save: (name: string, specs: ChartSpec[]) => Promise<void>;
  remove: (name: string) => Promise<void>;
} {
  const { user } = useAuth();
  const dispatch = useCommand();
  const { syncEntity } = useStore();
  const listVersion = useListVersion();
  const [views, setViews] = useState<SavedView[]>([]);
  const userId = user?.userId ?? null;

  // user.* events aren't in the regular sync scope — fetch them on demand so
  // the local users row exists before rebuildUser runs (otherwise the projection
  // bail-out blocks the dashboards write).
  useEffect(() => {
    if (!userId) return;
    syncEntity(userId).catch(() => {});
  }, [userId, syncEntity]);

  useEffect(() => {
    if (!userId) {
      setViews([]);
      return;
    }
    let cancelled = false;
    db
      .select({ dashboards: s.users.dashboards })
      .from(s.users)
      .where(eq(s.users.id, userId))
      .then((rows) => {
        if (cancelled) return;
        const json = rows[0]?.dashboards ?? null;
        setViews(parseSavedViews(json));
      })
      .catch(() => {
        if (!cancelled) setViews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, listVersion]);

  const writeAll = async (next: SavedView[]) => {
    if (!userId) throw new Error("Not authenticated");
    await dispatch("user.setDashboards", {
      uuid: userId,
      dashboards: JSON.stringify(next),
    });
  };

  const save = async (name: string, specs: ChartSpec[]) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const entry: SavedView = {
      name: trimmed,
      token: encodeDashboard(specs),
      savedAt: Date.now(),
    };
    const existing = views.findIndex((v) => v.name === trimmed);
    const next = [...views];
    if (existing >= 0) next[existing] = entry;
    else next.push(entry);
    await writeAll(next);
  };

  const remove = async (name: string) => {
    const next = views.filter((v) => v.name !== name);
    await writeAll(next);
  };

  return { views, save, remove };
}
